import * as React from "react";

import { Box } from "../../components/ui/box";
import { Input } from "../../components/ui/input";
import { Text, textVariants, type TextProps } from "../../components/ui/text";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type EditableTitleFieldProps = Omittable<{
  /** Current committed title, rendered as plain text at rest. */
  value: string;
  /** Persist the trimmed next value when the edit commits (Enter/blur). */
  onCommit: (next: string) => void;
  size?: TextProps["size"];
  weight?: TextProps["weight"];
  align?: TextProps["align"];
  /** Span the available width (card header, pushing controls out); omit to
   *  hug the text (dialog breadcrumb / heading). */
  fill?: boolean;
  /** Pull the field left by its horizontal hover padding so the title text
   *  aligns flush with the content below it, while the hover/edit outline
   *  still bleeds the same 4px outward. Used in the card header. */
  flushLeft?: boolean;
  /** When false, renders plain non-interactive text with no edit affordance. */
  editable?: boolean;
  placeholder?: string;
  "aria-label"?: string;
}>;

/**
 * An inline-editable title: plain text at rest that reveals a rounded
 * input-style outline on hover, and keeps that outline lit while it's being
 * edited (even after the pointer leaves) so the spot being edited stays
 * obvious. The border colors in from a permanently-reserved transparent 1px,
 * so nothing reflows. Enter/blur commit the trimmed value; Escape cancels.
 *
 * Used by widget cards and detail dialogs so every editable title reads the
 * same; `fill` adapts it to a full-width header vs. a content-hugging title.
 */
export function EditableTitleField({
  value,
  onCommit,
  size = "sm",
  weight = "semibold",
  align,
  fill,
  flushLeft,
  editable = true,
  placeholder = "Untitled",
  "aria-label": ariaLabel,
}: EditableTitleFieldProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const didSubmitRef = React.useRef(false);

  const startEditing = () => {
    if (!editable) return;
    didSubmitRef.current = false;
    setDraft(value);
    setEditing(true);
  };

  const finishEditing = (next: string) => {
    setEditing(false);
    const trimmed = next.trim();
    if (trimmed !== value) onCommit(trimmed);
  };

  const cancelEditing = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") cancelEditing();
    if (event.key === "Enter") {
      didSubmitRef.current = true;
      finishEditing(draft);
    }
  };

  return (
    <Box minWidth={0} rounded="md" borderColor="transparent" paddingX="xs" paddingY="2xs"
      className={cn(
        "border",
        fill && "w-full flex-1",
        flushLeft && "-ml-1",
        editable && !editing && "hover:border-input",
        editing && "border-ring",
      )}
    >
      {editing ? (
        // A bare Input wearing the SAME text classes as the resting Text (via
        // textVariants), so entering edit mode swaps the glyphs' owner without
        // changing their size, weight, or line box — the row never grows.
        // `bare` drops the primitive's shell and fixed metrics entirely.
        <Input
          bare
          value={draft}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(event.target.value)
          }
          onBlur={() => {
            if (!didSubmitRef.current) finishEditing(draft);
          }}
          onKeyDown={handleKeyDown}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          placeholder={placeholder}
          autoFocus
          className={cn(
            textVariants({ align, size, weight }),
            "w-full min-w-0 bg-transparent text-foreground outline-none placeholder:text-muted-foreground/60",
          )}
          aria-label={ariaLabel ?? "Edit title"}
        />
      ) : editable ? (
        <Box fill="width" minWidth={0} cursor="text"
          as="button"
          className="text-left"
          onClick={startEditing}
          aria-label={ariaLabel ?? "Edit title"}
        >
          <Text size={size} weight={weight} align={align} truncate>
            {value.length > 0 ? value : placeholder}
          </Text>
        </Box>
      ) : (
        <Text size={size} weight={weight} align={align} truncate>
          {value}
        </Text>
      )}
    </Box>
  );
}
